import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from 'sonner';
import axios from 'axios';
import {
  FileSignature,
  Send,
  RefreshCw,
  Download,
  FileText,
  Trash2,
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  Upload,
  User,
  Mail,
  Phone,
  Eye,
  Euro,
  Tag,
  Link,
  Copy,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getToken = () => localStorage.getItem('admin_token');
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const STATE_CONFIG = {
  DONE: { label: 'Firmato', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  WAIT_SIGN: { label: 'In attesa firma', color: 'bg-amber-100 text-amber-800', icon: Clock },
  WAIT_SIGNERS: { label: 'In attesa firma', color: 'bg-amber-100 text-amber-800', icon: Clock },
  WAIT_VALIDATION: { label: 'In validazione', color: 'bg-blue-100 text-blue-800', icon: Clock },
  ERROR: { label: 'Errore', color: 'bg-red-100 text-red-800', icon: XCircle },
  CANCELLED: { label: 'Annullato', color: 'bg-slate-100 text-slate-600', icon: XCircle },
  PENDING: { label: 'Inviato', color: 'bg-blue-100 text-blue-800', icon: Clock },
};

const getStateConfig = (state) => STATE_CONFIG[state] || { label: state || 'Sconosciuto', color: 'bg-slate-100 text-slate-600', icon: AlertTriangle };

export default function ESignDocuments({ isSuperAdmin }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [clients, setClients] = useState([]);
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [pricing, setPricing] = useState(null);

  const [form, setForm] = useState({
    file: null,
    title: '',
    description: '',
    signer_name: '',
    signer_surname: '',
    signer_email: '',
    signer_mobile: '',
  });

  const loadDocuments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/esign/documents`, { headers: authHeaders() });
      setDocuments(data.documents || []);
    } catch (err) {
      toast.error('Errore nel caricamento documenti');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, { headers: authHeaders() });
      setClients(data || []);
    } catch { /* ignore */ }
  }, []);

  const loadPricing = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/esign/pricing`, { headers: authHeaders() });
      setPricing(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadDocuments(); loadClients(); loadPricing(); }, [loadDocuments, loadClients, loadPricing]);

  const handleSend = async () => {
    if (!form.file) return toast.error('Seleziona un file PDF');
    if (!form.title.trim()) return toast.error('Inserisci un titolo');
    if (!form.signer_name.trim() || !form.signer_surname.trim()) return toast.error('Inserisci nome e cognome del firmatario');
    if (!form.signer_email.trim()) return toast.error('Inserisci l\'email del firmatario');

    setSending(true);
    const formData = new FormData();
    formData.append('file', form.file);
    formData.append('title', form.title);
    formData.append('description', form.description);
    formData.append('signer_name', form.signer_name);
    formData.append('signer_surname', form.signer_surname);
    formData.append('signer_email', form.signer_email);
    formData.append('signer_mobile', form.signer_mobile);

    try {
      const { data } = await axios.post(`${API}/admin/esign/send`, formData, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data.message || 'Documento inviato per la firma');
      setShowSendDialog(false);
      setForm({ file: null, title: '', description: '', signer_name: '', signer_surname: '', signer_email: '', signer_mobile: '' });
      loadDocuments();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail?.type === 'insufficient_credit') {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Errore nell\'invio');
      }
    } finally {
      setSending(false);
    }
  };

  const handleRefreshAll = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.post(`${API}/admin/esign/refresh-all`, {}, { headers: authHeaders() });
      toast.success(data.message || 'Stati aggiornati');
      loadDocuments();
    } catch (err) {
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setRefreshing(false);
    }
  };

  const handleCheckStatus = async (doc) => {
    try {
      const { data } = await axios.get(`${API}/admin/esign/status/${doc.id}`, { headers: authHeaders() });
      setSelectedDoc({ ...doc, ...data });
      loadDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel controllo stato');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/admin/esign/download/${doc.id}`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `firmato_${doc.filename || 'documento.pdf'}`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Download completato');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Documento firmato non ancora disponibile');
    }
  };

  const handleDownloadAudit = async (doc) => {
    try {
      const response = await axios.get(`${API}/admin/esign/audit/${doc.id}`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit_${doc.filename || 'trail.pdf'}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Audit trail non ancora disponibile');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Eliminare il documento "${doc.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/esign/documents/${doc.id}`, { headers: authHeaders() });
      toast.success('Documento eliminato');
      loadDocuments();
    } catch (err) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const selectClient = (client) => {
    setForm(prev => ({
      ...prev,
      signer_name: client.nome || '',
      signer_surname: client.cognome || '',
      signer_email: client.email || '',
      signer_mobile: client.telefono || '',
    }));
    setShowClientPicker(false);
  };

  const stats = {
    total: documents.length,
    signed: documents.filter(d => d.state === 'DONE').length,
    pending: documents.filter(d => ['WAIT_SIGN', 'WAIT_VALIDATION', 'PENDING'].includes(d.state)).length,
    error: documents.filter(d => d.state === 'ERROR').length,
  };

  const fesCost = pricing?.cost_table?.fes_otp_email;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="esign-documents-section">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileSignature className="w-7 h-7 text-indigo-600" />
            Firma Elettronica
          </h1>
          <p className="text-sm text-slate-500 mt-1">Invia documenti per la firma elettronica ai clienti</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRefreshAll} disabled={refreshing} data-testid="esign-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Aggiorna stati
          </Button>
          <Button size="sm" onClick={() => setShowSendDialog(true)} data-testid="esign-send-btn">
            <Send className="w-4 h-4 mr-1" />
            Invia documento
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Totale', value: stats.total, color: 'bg-slate-50 border-slate-200', textColor: 'text-slate-900' },
          { label: 'Firmati', value: stats.signed, color: 'bg-emerald-50 border-emerald-200', textColor: 'text-emerald-700' },
          { label: 'In attesa', value: stats.pending, color: 'bg-amber-50 border-amber-200', textColor: 'text-amber-700' },
          { label: 'Errori', value: stats.error, color: 'bg-red-50 border-red-200', textColor: 'text-red-700' },
        ].map(s => (
          <div key={s.label} className={`${s.color} border rounded-xl p-4`}>
            <p className="text-xs font-medium text-slate-500">{s.label}</p>
            <p className={`text-2xl font-bold ${s.textColor}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Pricing Section */}
      {pricing && (
        <div id="esign-pricing-section" className="bg-gradient-to-br from-indigo-50 to-slate-50 rounded-xl p-5 border border-indigo-200" data-testid="esign-pricing-section">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="w-5 h-5 text-indigo-600" />
            <h3 className="text-sm font-bold text-slate-800">Listino Servizi TelcaVoIP Firma Elettronica</h3>
            {isSuperAdmin && <Badge variant="outline" className="text-xs border-indigo-300 text-indigo-600">+{pricing.markup_pct}% markup</Badge>}
          </div>
          <div className="space-y-2">
            {Object.entries(pricing.cost_table).map(([key, val]) => (
              <div key={key} className="flex items-center justify-between bg-white rounded-lg p-3 border border-slate-100">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800">{val.label}</p>
                  <p className="text-xs text-slate-500">{val.description}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                  {isSuperAdmin && (
                    <div className="text-right">
                      <p className="text-xs text-slate-400">OpenAPI.it</p>
                      <p className="text-sm text-slate-400 line-through">{val.base_cost.toFixed(2)}</p>
                    </div>
                  )}
                  <div className="text-right">
                    <p className="text-xs text-indigo-500 font-medium">{isSuperAdmin ? 'TelcaVoIP' : 'Costo'}</p>
                    <p className="text-lg font-bold text-indigo-700">{val.telcavoip_cost.toFixed(2)}</p>
                  </div>
                  <Euro className="w-4 h-4 text-slate-400" />
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-3">Costo per firmatario. Il costo viene addebitato dal wallet TelcaVoIP al momento dell'invio. Il Super Admin non viene addebitato.</p>
        </div>
      )}

      {/* Documents Table */}
      {documents.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <FileSignature className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Nessun documento inviato</p>
          <p className="text-sm text-slate-400 mt-1">Clicca "Invia documento" per iniziare</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Documento</TableHead>
                  <TableHead>Firmatario</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Costo</TableHead>
                  <TableHead>Data invio</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map(doc => {
                  const stateConf = getStateConfig(doc.state);
                  const StateIcon = stateConf.icon;
                  return (
                    <TableRow key={doc.id} data-testid={`esign-doc-row-${doc.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate max-w-[200px]">{doc.title}</p>
                            <p className="text-xs text-slate-400 truncate max-w-[200px]">{doc.filename}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{doc.signer_name} {doc.signer_surname}</p>
                          <p className="text-xs text-slate-400">{doc.signer_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${stateConf.color} border-0 gap-1`}>
                          <StateIcon className="w-3 h-3" />
                          {stateConf.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {doc.cost_charged > 0 ? (
                          <span className="font-medium text-indigo-700">{doc.cost_charged.toFixed(2)}</span>
                        ) : (
                          <span className="text-slate-400 text-xs">Gratuito</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {doc.created_at ? new Date(doc.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleCheckStatus(doc)} title="Controlla stato" data-testid={`esign-check-${doc.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {doc.signer_url && doc.state !== 'DONE' && (
                            <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(doc.signer_url); toast.success('Link firma copiato!'); }} title="Copia link firma" data-testid={`esign-link-${doc.id}`}>
                              <Link className="w-4 h-4 text-indigo-600" />
                            </Button>
                          )}
                          {doc.state === 'DONE' && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)} title="Scarica firmato" data-testid={`esign-download-${doc.id}`}>
                                <Download className="w-4 h-4 text-emerald-600" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDownloadAudit(doc)} title="Scarica audit trail">
                                <FileText className="w-4 h-4 text-blue-600" />
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(doc)} title="Elimina" data-testid={`esign-delete-${doc.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Send Document Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-indigo-600" />
              Invia documento per firma
            </DialogTitle>
            <DialogDescription>
              Carica un PDF e inserisci i dati del firmatario.
              {fesCost && (
                <span className="block mt-1 font-medium text-indigo-600">
                  Costo: {fesCost.telcavoip_cost.toFixed(2)} per firmatario
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* File Upload */}
            <div>
              <Label>File PDF *</Label>
              <div className="mt-1">
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg p-4 cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors" data-testid="esign-file-upload">
                  <Upload className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-500">
                    {form.file ? form.file.name : 'Clicca per selezionare un PDF'}
                  </span>
                  <input
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={e => setForm(prev => ({ ...prev, file: e.target.files[0] || null }))}
                  />
                </label>
              </div>
            </div>

            {/* Title & Description */}
            <div>
              <Label>Titolo documento *</Label>
              <Input
                value={form.title}
                onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="es. Contratto di consulenza"
                data-testid="esign-title-input"
              />
            </div>
            <div>
              <Label>Descrizione</Label>
              <Input
                value={form.description}
                onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descrizione opzionale"
              />
            </div>

            {/* Signer Details */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">Firmatario</Label>
                {clients.length > 0 && (
                  <Button variant="outline" size="sm" onClick={() => setShowClientPicker(!showClientPicker)} data-testid="esign-pick-client-btn">
                    <User className="w-3 h-3 mr-1" />
                    Seleziona cliente
                  </Button>
                )}
              </div>

              {showClientPicker && (
                <div className="mb-3 max-h-40 overflow-y-auto border rounded-lg divide-y">
                  {clients.map(c => (
                    <button
                      key={c.id || c.email}
                      onClick={() => selectClient(c)}
                      className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm flex items-center gap-2"
                    >
                      <User className="w-3 h-3 text-slate-400" />
                      <span className="font-medium">{c.nome} {c.cognome}</span>
                      <span className="text-slate-400 text-xs">{c.email}</span>
                    </button>
                  ))}
                  {clients.length === 0 && <p className="text-xs text-slate-400 p-3">Nessun cliente registrato</p>}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nome *</Label>
                  <Input
                    value={form.signer_name}
                    onChange={e => setForm(prev => ({ ...prev, signer_name: e.target.value }))}
                    placeholder="Mario"
                    data-testid="esign-signer-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Cognome *</Label>
                  <Input
                    value={form.signer_surname}
                    onChange={e => setForm(prev => ({ ...prev, signer_surname: e.target.value }))}
                    placeholder="Rossi"
                    data-testid="esign-signer-surname"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-xs flex items-center gap-1"><Mail className="w-3 h-3" /> Email *</Label>
                <Input
                  type="email"
                  value={form.signer_email}
                  onChange={e => setForm(prev => ({ ...prev, signer_email: e.target.value }))}
                  placeholder="mario.rossi@email.com"
                  data-testid="esign-signer-email"
                />
              </div>
              <div className="mt-3">
                <Label className="text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> Telefono (opzionale)</Label>
                <Input
                  type="tel"
                  value={form.signer_mobile}
                  onChange={e => setForm(prev => ({ ...prev, signer_mobile: e.target.value }))}
                  placeholder="+39 123 456 7890"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>Annulla</Button>
            <Button onClick={handleSend} disabled={sending} data-testid="esign-confirm-send-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              {sending ? 'Invio in corso...' : 'Invia per firma'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Detail Dialog */}
      <Dialog open={!!selectedDoc} onOpenChange={() => setSelectedDoc(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Dettagli documento</DialogTitle>
            <DialogDescription>Informazioni sullo stato della firma</DialogDescription>
          </DialogHeader>
          {selectedDoc && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-slate-500">Titolo</p>
                  <p className="font-medium">{selectedDoc.title}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Stato</p>
                  <Badge className={`${getStateConfig(selectedDoc.state).color} border-0`}>
                    {getStateConfig(selectedDoc.state).label}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Firmatario</p>
                  <p>{selectedDoc.signer_name} {selectedDoc.signer_surname}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Email</p>
                  <p>{selectedDoc.signer_email}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Data invio</p>
                  <p>{selectedDoc.created_at ? new Date(selectedDoc.created_at).toLocaleString('it-IT') : '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Costo addebitato</p>
                  <p className="font-medium text-indigo-700">
                    {selectedDoc.cost_charged > 0 ? `${selectedDoc.cost_charged.toFixed(2)}` : 'Gratuito (Super Admin)'}
                  </p>
                </div>
              </div>
              {(selectedDoc.signer_url || selectedDoc.detail?.signers?.[0]?.url) && selectedDoc.state !== 'DONE' && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-indigo-700 mb-2">Link per la firma:</p>
                  <div className="flex items-center gap-2">
                    <input readOnly value={selectedDoc.signer_url || selectedDoc.detail?.signers?.[0]?.url} className="flex-1 text-xs bg-white border rounded px-2 py-1 text-slate-600" />
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(selectedDoc.signer_url || selectedDoc.detail?.signers?.[0]?.url); toast.success('Link copiato!'); }}>
                      <Copy className="w-3 h-3 mr-1" /> Copia
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">Condividi questo link con il firmatario se non ha ricevuto l'email</p>
                </div>
              )}
              {selectedDoc.state === 'DONE' && (
                <div className="flex gap-2 pt-2 border-t">
                  <Button size="sm" onClick={() => handleDownload(selectedDoc)}>
                    <Download className="w-4 h-4 mr-1" /> Scarica firmato
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDownloadAudit(selectedDoc)}>
                    <FileText className="w-4 h-4 mr-1" /> Audit trail
                  </Button>
                </div>
              )}
              {selectedDoc.state === 'ERROR' && selectedDoc.detail?.errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-red-700">Errore: {selectedDoc.detail.errorMessage}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
